import type {OverlayPanel} from "primereact/overlaypanel";
import {Tooltip} from "primereact/tooltip";
import {type MouseEvent, useRef, useState} from "react";
import {useSelector} from "react-redux";

import {
    type AppreciationKey,
    getAppreciationId,
    selectAppreciationHasValueCurried,
    selectAppreciationIsVisibleCurried,
    selectAppreciationValueOrDefaultCurried,
} from "../../redux/appreciationsUtil";
import {selectReadOnly} from "../../redux/persistenceSlice";
import AppreciationOverlay from "./AppreciationOverlay";
import {resolveAppreciationId, useAppreciationVisible} from "./appreciationHooks";

interface AppreciationSectionIconProps {
    /** option1: provide appreciation key */
    appreciationKey?: AppreciationKey;
    /** option2: provide appreciation page (and tab and section), to calculate key */
    page?: string;
    tab?: string;
    section?: string;
    /** Displayed placeholder */
    placeholder?: string;
}

/**
 * React component for an Appreciation Icon in the section headers, which open an overlay to the appreciation on click.
 */
export default function AppreciationSectionIcon({
    appreciationKey,
    page,
    tab,
    section,
    placeholder,
}: AppreciationSectionIconProps) {
    const effectiveAppreciationKey = resolveAppreciationId(appreciationKey, page, tab, section);
    const storedValue = useSelector(selectAppreciationValueOrDefaultCurried(effectiveAppreciationKey));

    const overlayRef = useRef<OverlayPanel>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);

    const buttonVisible = useSelector(selectAppreciationIsVisibleCurried(effectiveAppreciationKey));
    const hasValue = useSelector(selectAppreciationHasValueCurried(effectiveAppreciationKey));
    const readOnly = useSelector(selectReadOnly);
    const appreciationVisible = useAppreciationVisible(effectiveAppreciationKey);

    const appreciationId = getAppreciationId(effectiveAppreciationKey);

    /** Unsichtbarer Anker in der Überschrift, an dessen linker Kante PrimeReact das Overlay ausrichtet. */
    const anchorRef = useRef<HTMLSpanElement>(null);
    const [overlayLayout, setOverlayLayout] = useState<OverlayLayout | undefined>(undefined);

    const onToggleButtonClick = (e: MouseEvent<HTMLSpanElement>) => {
        if (overlayRef.current && anchorRef.current) {
            setOverlayLayout(overlayLayoutForSection(e.currentTarget));
            overlayRef.current.toggle(e, anchorRef.current);
        }
    };

    const onShow = () => {
        setOverlayVisible(true);
    };

    const onHide = () => {
        setOverlayVisible(false);
    };

    // Appreciation UI requires permission, except for the always-visible exception fields (#18607)
    if (!appreciationVisible || (!buttonVisible && !readOnly) || (readOnly && !hasValue)) {
        return null;
    }

    const classNameIcon = "pi pi-file-edit";
    const classNameColor = overlayVisible ? "text-muted-color" : "text-primary-500";
    const hasTodo = storedValue && storedValue?.match(/\btodo\b/i) != null;
    let classNamePreview =
        "text-muted-color text-xs font-normal max-w-64 whitespace-nowrap overflow-hidden text-ellipsis";
    classNamePreview += hasTodo ? " border rounded-sm border-red-500 p-1" : "";

    return (
        <>
            <span
                ref={anchorRef}
                aria-hidden="true"
                className="absolute bottom-0 w-0 h-0"
                style={{right: overlayLayout?.anchorRight ?? 0}}
            />
            <span className={classNamePreview}>{storedValue}</span>
            <span
                id={`appreciationIcon_${appreciationId}`}
                className={`pl-0 mx-1 cursor-pointer ${classNameIcon} ${classNameColor}`}
                onClick={onToggleButtonClick}
            />
            <Tooltip target={`#appreciationIcon_${appreciationId}`} position="left" className="appreciation-tooltip">
                {!storedValue ? (
                    "Kein Würdigungstext hinterlegt."
                ) : (
                    <>
                        <b>{hasTodo ? "Würdigungstext muss bearbeitet werden:" : "Würdigungstext:"}</b>
                        <br />
                        <br />
                        <i>{storedValue.startsWith("TODO!") ? storedValue.substring(6) : storedValue}</i>
                    </>
                )}
            </Tooltip>
            <AppreciationOverlay
                appreciationKey={appreciationKey}
                page={page}
                tab={tab}
                section={section}
                ref={overlayRef}
                placeholder={placeholder}
                onHide={onHide}
                onShow={onShow}
                width={overlayLayout?.width}
                arrowAtRight={overlayLayout != null}
            />
        </>
    );
}

/** Mindestbreite des Würdigungs-Overlays, auch in sehr schmalen Abschnitten. */
const MIN_OVERLAY_WIDTH_PX = 320;

/** Breite des Würdigungs-Overlays und Abstand seines Ankers vom rechten Rand der Überschrift. */
interface OverlayLayout {
    width: string;
    anchorRight: string;
}

/**
 * Breite und Lage des Würdigungs-Overlays: höchstens 50vw, nicht breiter als der Abschnitt (Überschrift bis
 * einschließlich Symbol), rechtsbündig am Symbol. PrimeReact richtet das Panel am Anker linksbündig aus und
 * klemmt es sonst rechtsbündig an den linken Fensterrand – über die Seitenleiste. Deshalb sitzt ein
 * unsichtbarer Null-Breiten-Anker (in der `relative` Überschrift) genau eine Panelbreite links vom Symbol;
 * der Pfeil steht per CSS rechts über dem Symbol. Ein Klick auf Titel oder Chevron trifft den Anker nicht
 * und schließt das Overlay weiterhin als Außenklick.
 * @param icon angeklicktes Symbol
 */
function overlayLayoutForSection(icon: HTMLElement): OverlayLayout {
    const heading = icon.parentElement;
    const iconRect = icon.getBoundingClientRect();
    const headingRect = heading ? heading.getBoundingClientRect() : iconRect;
    const sectionWidth = Math.floor(iconRect.right - headingRect.left);
    const width = Math.max(MIN_OVERLAY_WIDTH_PX, Math.min(Math.floor(window.innerWidth / 2), sectionWidth));
    const iconInset = Math.max(0, Math.floor(headingRect.right - iconRect.right));
    return {width: `${width}px`, anchorRight: `${width + iconInset}px`};
}
